<template>
  <div data-component="km-offline-login-schema">
  <div v-if="visible" class="km-offline-login-schema">
    <p class="header w-100 text-center">KMOffline Login Process</p>

    <km-offline-status-schema :stage="stage"/>
    <div style="display: flex; align-items: center; align-content: center;" class="w-100">
        <div class="my-0 mx-auto text-left py-1 px-3">
        <table class="steps-table table b-table table-sm b-table-caption-top mb-0">
            <tr v-for="(step, index) in steps" :key="index">
              <td :class="{'current-step': step.isCurrent}" 
                  nowrap 
                  v-if="steps.length > 1"
              > Step {{index + 1}} - </td>
              <td :class="{'current-step': step.isCurrent, 'clickable': step.isNext, 'error': step.isError}"
                  @click="step.onClick"
              >{{step.caption}}</td>
            </tr>
            <tr>
            <td colspan="2" v-if="!soloBackendNotRunning && !registrationRequired && connectivityOk">
                <input type="checkbox" v-model="resetOfflinePassword" value="resetOfflinePassword" />
                <small class="ml-2">Reset KMOffline Password</small>
            </td>
            </tr>
        </table>
        </div>
    </div>
    <b-row no-gutters>
      <b-col v-if="hasNext" cols="auto" class="ml-auto">
        <b-button variant="link" class="pt-0 pr-3" @click="nextStepAction">{{$t('result.navigate.next')}}</b-button>
      </b-col>
    </b-row>
  </div>
  </div>
</template>

<script>
//---- components ---------
import KmOfflineStatusSchema from "./KmOfflineStatusSchema.vue"
//---- mixins -------------
import KmOfflineStatusMixin from "./KmOfflineStatusMixin.js"
//---- services -----------
//---- other --------------
import routes from '../../routes.js';
//---- 3rd-parties --------

/**
 * This component is the one shown on the login screen when logging-in for kmoffline.
 * 
 * It displays a schema showing the connectivity status as well as a list of steps 
 * for the authentication process.
 */
export default {

  name: "km-offline-login-walkthrough",
  
  components: {
    KmOfflineStatusSchema,
  },

  mixins: [KmOfflineStatusMixin],

  data() {
    return {
      resetOfflinePassword: false
    }
  },

  computed: {

    hserviceOk() {
      return !this.isKMOffline || this.hServiceConnectivity;
    },

    internetOk() {
      return !this.isKMOffline || this.backendInternetConnectivity;
    },

    connectivityOk() {
      return this.hserviceOk && this.internetOk;
    },

    //---------------------------------------------------

    // used for online to know if we should display the component or not
    visible() {
      return this.isKMOffline || this.hasKMOfflineAuthHash || this.hasKMOfflineRegistrationHash;
    },

    // used for online to know if we should display the component or not
    hasKMOfflineAuthHash() {
      const currentHash = routes.currentRoute.hash;
      return currentHash.includes("kmOfflineAuth");
    },

    currentStep() {
      if (this.isKMOffline)
        return 1;
      if (this.hasKMOfflineAuthHash || this.hasKMOfflineRegistrationHash)
        return 2;
      return 1; // dummy
    },

    hasNext() {
      return this.currentStep < this.steps.length;
    },

    //---------------------------------------------------

    stage() {
      return this.steps[this.currentStep - 1].location;
    },

    nextStepAction() {
      return this.steps.find(x => x.isNext)?.onClick || this.noop;
    },

    // returns a list of steps needed for the authentication
    steps() {

      // if the solo backend is not running
      if (this.soloBackendNotRunning) {
        return [ 
          {
            isCurrent: true,
            isNext: false,
            isError: true,
            onClick: this.noop,
            location: "laptop",
            caption: "Your local KMOffline Server is not started, please run Start-PTC-SKD.bat to use KMOffline."
          }
        ];
      }

      // if we're disconnected, a single step is required
      if (!this.connectivityOk) {
        if (this.registrationRequired)
          return [        
            {
              isCurrent: true,
              isNext: false,
              onClick: this.noop,
              location: "laptop",
              caption: "You need to be connected to perform first time synchronization.",
            },
          ];
        else 
          return [        
            {
              isCurrent: true,
              isNext: true,
              onClick: this.signIn,
              location: "laptop",
              caption: "Login using your KMOffline password",
            },
          ];
      }

      // if we're connected - first step is access km-offline
      const steps = [
        {
          onClick: this.noop,
          location: "laptop",
          caption: "Access KMOffline",
        },
      ];

      const action = this.registrationRequired || this.resetOfflinePassword ? 'register-online' : 'login-online';

      // second step is to login to advisor online
      steps.push({
          onClick: () => this.$emit(action),
          location: "server",
          caption: "Login to SKD Online, using on online password",
        });

      // if registration or password reset is required, next step is to create pwd
      if (this.registrationRequired || this.resetOfflinePassword) {
        steps.push({
            onClick: this.signIn,
            location: "server",
            caption: "Create your KMOffline password",
        });
      }

      // last step is synchronization
      steps.push({
          onClick: this.signIn,
          location: "laptop",        
          caption: "Synchronize"
        });

      steps.forEach((step, i) => {
        step.isCurrent = this.currentStep == i + 1;
        step.isNext = this.currentStep == i;
      })

      return steps;
    }  // end steps
  }, // end computed

  //---------------------------------------------------------------------------

  methods: {
    noop() {},

    signIn() {
      this.$emit('guide-user-sign-in');
    },

  } // end methods
};
</script>

<style scoped>

.header {
  font-weight: 600;
}
.step {
  margin-bottom: .2rem;
}

.current-step {
  font-weight: 600;
  color: #0060df; /* fallback for IE */
  color: var(--fbc-blue-60);
}

.steps-table tr:first-child td {
  border-top: none;
}

.km-offline-login-schema {
  padding: 1rem 0 0 0;
  margin: 1rem;
  margin-bottom: 2rem;
  border: 1px solid black;
  background-color: #f8f8f8;
}

.text-disabled {
  color: #cccccc;
}

.caption {
  font-weight: 400;
  font-size: smaller;
}

.error {
  color: red;
}
</style>